/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { getStatusBtnStyle } from "./ReviewsHelper";
import { useCallback, useMemo, useRef } from "react";

import SlickGrid1, { withDynamicColumns, type SlickGrid1Ref } from "@CubeComponents/html/SlickGrid1";
import { convertDate } from "@Services/luxonHelper";
import { resourceActivityInReviewUrl } from "@Services/dataManager";
import OpenFileInViewerIcon from "@Assets/icons/OpenInViewer.svg";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { getViewerUrl } from "@Helpers";

import { renderUserTag } from "@CubeComponents/html/userTagDom";
import { portalCreateRoot } from "@CubeComponents/html/cellPortals";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import { untranslated } from "@i18n";

/**
 * One row of `POST /reviews/getResourceActivityInReview`. SERVER GAP:
 * `getResourceActivityInReviewResponse` is `reviewStatusResponse`, whose `result` is `z.unknown()`, so
 * there is no server row type to flow. Shape taken from `getFileActivityPipeline`'s final `$project`.
 * `fileDetails` / `userDetails` are non-optional because the pipeline `$unwind`s both without
 * `preserveNullAndEmptyArrays`, so rows lacking either are dropped. A `type` alias so the grid's
 * `Record<string, unknown>`-style row constraints are satisfied.
 */
type ResourceActivityRow = {
  _id?: string;
  fileVersionId?: string;
  type?: string;
  createdDate?: number;
  fileDetails: { name?: string; size?: number; versionNumber?: number; fileVersionId?: string };
  /** Only present on `review_resource_updated` rows. */
  fileDetailsUpdatedTo?: { versionNumber?: number };
  userDetails: { userId?: ObjectIdString; fullName?: string; name?: string };
  status?: { _id?: string; name?: string; category?: string };
};

interface ResourceActivityInReviewProps {
  readonly reviewId: ObjectIdString;
  readonly spaceId?: string;
  readonly projectId?: string;
}

const ResourceActivityInReview = ({ reviewId, spaceId, projectId }: ResourceActivityInReviewProps) => {
  const fileSizeOptions = [
    { name: "KB", divideValue: 1024 },
    { name: "MB", divideValue: 1048576 },
  ];

  const query = useMemo(
    () => ({
      params: {
        reviewId,
      },
    }),
    [reviewId],
  );
  const dataGrid = useRef<SlickGrid1Ref<ResourceActivityRow> | null>(null);

  const openFileInViewer = (fileVersionId: string | undefined) => {
    const viewerUrl = getViewerUrl({ fileVersionId, spaceId, projectId });
    window.open(viewerUrl, "_blank");
  };

  const getFileSizeOptionIndex = (size: number) => {
    const index = size > fileSizeOptions[1]!.divideValue ? 1 : 0;
    return index;
  };

  const openFileInViewerFormatter = (
    row: number,
    cell: number,
    value: unknown,
    columnDef: unknown,
    dataContext: ResourceActivityRow,
  ) => {
    const container = document.createElement("div");
    container.className = "cursor-pointer";
    container.dataset.fileversionid = dataContext.fileVersionId;
    // The row is already in scope here, so the handler takes the id directly instead of reading it
    // back off `dataset` (which would need `currentTarget` narrowed to an element).
    container.onclick = () => {
      openFileInViewer(dataContext.fileVersionId);
    };
    const img = document.createElement("img");
    img.src = OpenFileInViewerIcon;
    container.appendChild(img);
    return container;
  };

  const fileNameFormatter = (
    row: number,
    cell: number,
    value: unknown,
    columnDef: unknown,
    dataContext: ResourceActivityRow,
  ) => {
    const { fileDetails } = dataContext;
    const container = document.createElement("div");
    // A filename is content, not copy, so it goes through `untranslated()` — the sanctioned brand
    // for dynamic values — rather than being asserted to `TranslatedString`.
    portalCreateRoot(container).render(
      <ToolTipComponent
        showTipPointer
        position="bottom"
        content={fileDetails.name === undefined ? undefined : untranslated(fileDetails.name)}
      >
        <p>{fileDetails.name}</p>
      </ToolTipComponent>,
    );
    return container;
  };

  const fileSizeFormatter = (
    row: number,
    cell: number,
    value: unknown,
    columnDef: unknown,
    dataContext: ResourceActivityRow,
  ) => {
    const { size } = dataContext.fileDetails;
    // `size` is optional on the projected version sub-document; the arithmetic below already
    // produced NaN for a version without one, so the assertions only silence the types.
    const fileSizeIndex = getFileSizeOptionIndex(size!);
    const fileSizeOption = fileSizeOptions[fileSizeIndex];
    const formattedValue = parseFloat(String(size! / fileSizeOption!.divideValue)).toFixed(2);
    return `${formattedValue} ${fileSizeOption!.name}`;
  };

  const versionFormatter = (
    row: number,
    cell: number,
    value: unknown,
    columnDef: unknown,
    dataContext: ResourceActivityRow,
  ) => {
    const { fileDetails } = dataContext;
    return `V${fileDetails.versionNumber}`;
  };

  const creatorFormatter = (
    row: number,
    cell: number,
    value: unknown,
    columnDef: unknown,
    dataContext: ResourceActivityRow,
  ) => {
    const container = document.createElement("div");
    renderUserTag(container, { id: dataContext.userDetails.userId });
    return container;
  };

  const dateFormatter = (
    row: number,
    cell: number,
    value: unknown,
    columnDef: unknown,
    dataContext: ResourceActivityRow,
  ) => {
    const dateFormat = convertDate(Number(dataContext.createdDate), "dateForTooltip");
    const container = document.createElement("div");
    container.className = "inline-flex";
    portalCreateRoot(container).render(
      <ToolTipComponent showTipPointer position="bottom" content={dateFormat}>
        <span>{convertDate(Number(dataContext.createdDate), "dateForText")}</span>
      </ToolTipComponent>,
    );
    return container;
  };

  const statusFormatter = (
    row: number,
    cell: number,
    value: unknown,
    columnDef: unknown,
    dataContext: ResourceActivityRow,
  ) => {
    if (!dataContext.status?._id) {
      return "-";
    }
    const style = getStatusBtnStyle(dataContext.status!.category);
    const container = document.createElement("span");
    container.className = "rounded py-1 px-4 text-sm text-white";
    Object.assign(container.style, style);
    container.textContent = dataContext.status!.name!;
    return container;
  };

  const modificationTypeFormatter = (
    row: number,
    cell: number,
    value: unknown,
    columnDef: unknown,
    dataContext: ResourceActivityRow,
  ) => {
    const { type, fileDetailsUpdatedTo } = dataContext;

    if (type === "review_resource_added") {
      return "Added";
    }
    if (type === "review_resource_removed") {
      return "Removed";
    }
    if (type === "review_resource_updated") {
      return `Updated To V${fileDetailsUpdatedTo!.versionNumber}`;
    }
    return "";
  };

  // `withDynamicColumns` rather than `typedColumns`: most of these columns address nested fields by
  // dotted path ("fileDetails.name", "userDetails.userId", …), which can never be a
  // `keyof ResourceActivityRow`.
  const columnDefinitions = useMemo(
    () =>
      withDynamicColumns<ResourceActivityRow>([
        {
          id: "openInViewer",
          field: "fileVersionId",
          name: "",
          minWidth: 50,
          maxWidth: 50,
          formatter: openFileInViewerFormatter,
          disableFiltering: true,
          disableSorting: true,
          disableGrouping: true,
        },
        {
          id: "fileName",
          field: "fileDetails.name",
          name: "File Name",
          minWidth: 200,
          formatter: fileNameFormatter,
          clipMode: "EllipsisWithTooltip",
        },
        {
          id: "size",
          field: "fileDetails.size",
          name: "Size",
          minWidth: 100,
          formatter: fileSizeFormatter,
        },
        {
          id: "version",
          field: "fileDetails.versionNumber",
          name: "Version",
          minWidth: 80,
          formatter: versionFormatter,
        },
        {
          id: "creator",
          field: "userDetails.userId",
          name: "Creator",
          minWidth: 150,
          formatter: creatorFormatter,
          _valueGetter: (row: ResourceActivityRow) => row.userDetails.userId,
          _excelValueGetter: (row: ResourceActivityRow) =>
            row.userDetails.fullName || row.userDetails.name || row.userDetails.userId || "",
          disableFiltering: true,
          disableSorting: true,
          disableGrouping: true,
        },
        {
          id: "createdDate",
          field: "createdDate",
          name: "Date",
          minWidth: 150,
          formatter: dateFormatter,
          filterType: "Date",
        },
        {
          id: "status",
          field: "status.name",
          name: "Status",
          minWidth: 120,
          formatter: statusFormatter,
          _valueGetter: (row: ResourceActivityRow) => row.status?.name,
        },
        {
          id: "modificationType",
          field: "type",
          name: "Modification Type",
          minWidth: 150,
          formatter: modificationTypeFormatter,
        },
      ]),

    [],
  );

  const getDataGrid = useCallback(
    () => (
      <SlickGrid1
        ref={dataGrid}
        remoteDataSource={resourceActivityInReviewUrl}
        query={query}
        columnDefinitions={columnDefinitions}
        rowHeight={60}
        gridHeight="100%"
        enableFilteringOption
        enableSortingOption
        infiniteScroll
      />
    ),
    [query, columnDefinitions],
  );

  return getDataGrid();
};

export default ResourceActivityInReview;
